import * as React from 'react';
import { Accordion, Loader } from 'vet-bones/design-system/components';
import { isEqual } from 'vet-bones/utils';

import { refreshDataSourceTables } from 'src/actions/data-sources/action-creators/refreshDataSourceTables';
import { dataSourcesStardogRequestDispatchers } from 'src/actions/data-sources/actionCreators';
import { SelectionPanelContainer } from 'src/components/dataSourceSelection/SelectionPanelContainer';
import { PanelProps } from 'src/components/panelStack/PanelStack';
import { PanelWithSidebar } from 'src/components/resourceDialog/PanelWithSidebar';
import {
  PreviewErrorState,
  PreviewLoadingState,
  PreviewNoSelectionState,
} from 'src/components/resourceDialog/ResourcePreviewStates';
import {
  DataSourceTable,
  getDataSourceTableKey,
} from 'src/constants/dataSourceTable';
import {
  DataSourceOption,
  DataSourceType,
  SAMPLE_MUSIC_DATA_SOURCE_TYPE,
  getPreviewsDisabled,
} from 'src/constants/dataSourceTypeDescription';
import { SAMPLE_MUSIC_DATA_SOURCE } from 'src/constants/sampleDataSource';
import { TEST_IDS } from 'src/constants/testIds';
import { ConnectionSelectionStep } from 'src/containers/ProjectNewResourceDialog/components/ConnectionSelectionStep';
import { useProjectResourceDialog } from 'src/containers/ProjectNewResourceDialog/context/useProjectResourceDialog';
import { useEditedFields } from 'src/containers/ProjectNewResourceDialog/hooks/useEditedFields';
import { useMetadataFetchQueue } from 'src/containers/ProjectNewResourceDialog/hooks/useMetadataFetchQueue';
import { useTableChecking } from 'src/containers/ProjectNewResourceDialog/hooks/useTableChecking';
import { VgTableMetadata } from 'src/containers/ProjectNewResourceDialog/hooks/useVirtualGraphDialogState';
import { AdvancedOptions } from 'src/containers/ProjectVirtualGraphPreviewDialog/components/AdvancedOptions';
import { VirtualGraphOptionsForm } from 'src/containers/ProjectVirtualGraphPreviewDialog/components/VirtualGraphOptionsForm';
import { VirtualGraphPreview } from 'src/containers/ProjectVirtualGraphPreviewDialog/components/VirtualGraphPreview';
import { VirtualGraphTableSelection } from 'src/containers/ProjectVirtualGraphPreviewDialog/components/VirtualGraphTableSelection';
import { useRefreshDataSourceTypeMetadata } from 'src/containers/ProjectVirtualGraphPreviewDialog/hooks/useRefreshDataSourceTypeMetadata';
import { DataSourceCreationPanel } from 'src/containers/ProjectVirtualGraphPreviewDialog/panels/DataSourceCreationPanel';
import { connectionSelector } from 'src/selectors/connection';
import { dataSourceNativeQuoteCharacterSelector } from 'src/selectors/dataSourceNativeQuoteCharacter';
import { dataSourcesByTypeSelector } from 'src/selectors/dataSourcesByType';
import { isStardogFeatureSupportedSelector } from 'src/selectors/isStardogFeatureSupported';
import { virtualGraphPropertiesSelector } from 'src/selectors/virtualGraphProperties';
import { DataSource, VirtualGraphOption } from 'src/store/state/dataSources';
import {
  ProjectVirtualGraph,
  ProjectVirtualGraphOptions,
} from 'src/store/state/projects';
import * as copy from 'src/templates/copy';
import { catalogColumnsToFields } from 'src/templates/queries/catalogTableColumns';
import { generateSqlQueryForTable } from 'src/utils/generateSqlQueryForTable';
import { useCatalogTableColumns } from 'src/utils/hooks/useCatalogTableColumns';
import { useDialogPanelSetup } from 'src/utils/hooks/useDialogPanelSetup';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useProjectDataSourceQuery } from 'src/utils/hooks/useProjectDataSourceQuery';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useSampleMusicDataSource } from 'src/utils/hooks/useSampleMusicDataSource';

import styles from 'src/components/dataSourceSelection/selectionPanelContainer.module.scss';

const EMPTY_FIELDS: ProjectVirtualGraph['fields'] = [];

export type AddDataSourcePanelProps = {
  dataSourceType: DataSourceType;
  initialPreviewTable?: string;
  initialCustomQuery?: string; // Name of the custom query to preview
  skipToTables?: boolean; // Skip connection selection and go directly to tables step
  dataSourceId?: string; // Specific data source ID to use (for skipToTables)
};

type PanelStep = 'connection' | 'tables';

const findDataSourceInRedux = (
  dataSourceId: string,
  dataSourceType: DataSourceType,
  dataSourcesByType: Record<string, DataSource[]>,
  dataSources: DataSource[]
): DataSource | undefined => {
  if (dataSourceType === SAMPLE_MUSIC_DATA_SOURCE_TYPE) {
    const mysqlDataSources =
      dataSourcesByType[SAMPLE_MUSIC_DATA_SOURCE.type] || [];
    return mysqlDataSources.find((ds) => ds.id === dataSourceId);
  }
  return dataSources.find((ds) => ds.id === dataSourceId);
};

export const AddDataSourcePanel: React.VFC<
  PanelProps<AddDataSourcePanelProps>
> = ({
  closePanel,
  openPanel,
  dataSourceType,
  initialPreviewTable,
  initialCustomQuery,
  skipToTables,
  dataSourceId,
}) => {
  const dispatch = useReduxDispatch();
  const {
    setDialogTitle,
    setHeaderBackButton,
    setFullSize,
    setFooterOptions,
    selectedDataSource: contextDataSource,
    setSelectedDataSource,
    setSelectedDataSourceType,
    checkedVgTables,
    setCheckedVgTables,
    customSqlQueries,
    setCustomSqlQueries,
    setVgPreviewItem,
    vgTableMetadata,
    setVgTableMetadata,
    virtualGraphOptions,
    setVirtualGraphOptions,
    advancedVgOptions,
    setAdvancedVgOptions,
    resetDialogState,
  } = useProjectResourceDialog();

  const connection = useReduxSelector(connectionSelector);
  const dataSourcesByType = useReduxSelector(dataSourcesByTypeSelector);
  const {
    createOrRefresh: createOrRefreshSampleDataSource,
    isLoading: isSampleLoading,
    existingDataSource: existingSampleDataSource,
  } = useSampleMusicDataSource();

  const [step, setStep] = React.useState<PanelStep>(
    skipToTables ? 'tables' : 'connection'
  );
  const [localDataSource, setLocalDataSource] =
    React.useState<DataSource | null>(null);
  const [isConnecting, setIsConnecting] = React.useState(false);
  const [isUsingCustomQuery, setIsUsingCustomQuery] = React.useState(false);
  const [expandAllTables, setExpandAllTables] = React.useState(false);

  const [previewTable, setPreviewTable] = React.useState<string | null>(null);
  const [displayPreviewTable, setDisplayPreviewTable] = React.useState<
    string | null
  >(null);
  const [previewData, setPreviewData] = React.useState<{
    fields: ProjectVirtualGraph['fields'];
    previewRows: ProjectVirtualGraph['previewRows'];
    allResultsIncluded: boolean;
  } | null>(null);

  const [customQueryText, setCustomQueryText] = React.useState('');
  const [submittedCustomQuery, setSubmittedCustomQuery] = React.useState<
    string | null
  >(null);
  const [forceRefresh, setForceRefresh] = React.useState(false);
  const [errorText, setErrorText] = React.useState('');

  // Resource Properties accordion state (UI only - options come from context)
  const [isPropertiesOpen, setIsPropertiesOpen] = React.useState(false);
  const [isAdvancedOpen, setIsAdvancedOpen] = React.useState(false);

  // Edited field state and handlers
  const {
    editedResourceName,
    setEditedResourceName,
    editedFieldLabels,
    setEditedFieldLabels,
    handleResourceNameChange,
    handleFieldLabelChange,
    currentResourceName,
    currentFields,
  } = useEditedFields({
    previewTable,
    isUsingCustomQuery,
    setVgTableMetadata,
    submittedCustomQuery,
    previewData,
  });

  const lastProcessedQueryRef = React.useRef<string | null>(null);
  const lastInitializedPreviewTableRef = React.useRef<string | null>(null);
  const lastInitializedCustomQueryRef = React.useRef<string | null>(null);
  const hasSetForceRefreshForInitialTableRef = React.useRef(false);

  const isManualPreviewRef = React.useRef(false);

  const dataSources = React.useMemo(
    () => dataSourcesByType[dataSourceType] || [],
    [dataSourcesByType, dataSourceType]
  );

  // Compute the effective data source for initialization effects
  // For sample music data source, contextDataSource might be null if it was just created
  const effectiveDataSource = React.useMemo(
    () =>
      contextDataSource ||
      (dataSourceType === SAMPLE_MUSIC_DATA_SOURCE_TYPE
        ? dataSourcesByType[SAMPLE_MUSIC_DATA_SOURCE.type]?.find(
            (ds) => ds.id === SAMPLE_MUSIC_DATA_SOURCE.id
          )
        : null),
    [contextDataSource, dataSourceType, dataSourcesByType]
  );

  // The Redux copy of the active data source carries the up-to-date options
  // (localDataSource may lag). Used to learn whether previews are disabled.
  const reduxDataSource = React.useMemo(
    () =>
      localDataSource
        ? findDataSourceInRedux(
            localDataSource.id,
            dataSourceType,
            dataSourcesByType,
            dataSources
          )
        : undefined,
    [localDataSource, dataSourceType, dataSourcesByType, dataSources]
  );

  const dataSourceOptions =
    reduxDataSource?.options ?? localDataSource?.options;
  const previewsDisabled = getPreviewsDisabled(dataSourceOptions);
  // Until options load we cannot tell whether previews are disabled; treat the
  // preview query as not-ready so we never fire it on a skip-preview source
  // during the load window.
  const optionsLoaded = Boolean(dataSourceOptions);

  // Ensure the active data source's options are loaded (they are only fetched
  // for the connection step below, so skipToTables/preview flows can miss
  // them) so previewsDisabled is accurate.
  React.useEffect(() => {
    const activeDataSource = reduxDataSource || localDataSource;
    if (connection && activeDataSource && !activeDataSource.options) {
      dataSourcesStardogRequestDispatchers.requestDataSourceOptions(
        activeDataSource.id,
        connection
      );
    }
  }, [connection, reduxDataSource, localDataSource]);

  const isCatalogSupported = useReduxSelector(
    isStardogFeatureSupportedSelector('isCatalogSupported', connection)
  );

  const {
    columnsByTableKey: catalogColumnsByTableKey,
    error: catalogError,
    isLoading: isCatalogLoading,
    refetch: refetchCatalogColumns,
  } = useCatalogTableColumns({
    dataSourceId: localDataSource?.id,
    enabled: previewsDisabled,
  });

  // On skip-preview sources column headers come from the catalog. If the
  // catalog can't be reached at all (unsupported on this connection, or the
  // fetch errored) no table can be mapped — a connection-level problem that
  // gets its own message, distinct from a single table that has no columns.
  const isCatalogUnavailable =
    previewsDisabled &&
    !isCatalogLoading &&
    (catalogError || !isCatalogSupported);

  // Once the catalog has loaded successfully we know which tables have no
  // columns — those can't be mapped, so we surface a notice and block adding
  // them.
  const isCatalogReady =
    previewsDisabled &&
    !isCatalogLoading &&
    !catalogError &&
    isCatalogSupported;
  const tableHasNoCatalogColumns = React.useCallback(
    (tableKey: string) =>
      isCatalogReady && !catalogColumnsByTableKey[tableKey]?.length,
    [isCatalogReady, catalogColumnsByTableKey]
  );

  const handleMetadataRefreshComplete = React.useCallback(() => {
    setVgTableMetadata({});
    lastProcessedQueryRef.current = null;
    setForceRefresh(true);
    // The server-side metadata refresh rebuilds the catalog; re-fetch the
    // catalog columns so a "No columns available" table updates without a
    // page reload.
    if (previewsDisabled) {
      refetchCatalogColumns();
    }
  }, [previewsDisabled, refetchCatalogColumns, setVgTableMetadata]);

  const {
    isRefreshingMetadata,
    isMetadataRefreshable,
    refreshMetadata: refreshDataSourceTypeMetadata,
  } = useRefreshDataSourceTypeMetadata(
    localDataSource?.id,
    handleMetadataRefreshComplete
  );

  // Effect 1: Reset refs when props are cleared
  React.useEffect(() => {
    if (!initialPreviewTable) {
      lastInitializedPreviewTableRef.current = null;
      hasSetForceRefreshForInitialTableRef.current = false;
    }
  }, [initialPreviewTable]);

  React.useEffect(() => {
    if (!initialCustomQuery) {
      lastInitializedCustomQueryRef.current = null;
    }
  }, [initialCustomQuery]);

  // Effect 2: Initialize for table preview mode
  React.useEffect(() => {
    if (
      !initialPreviewTable ||
      !effectiveDataSource ||
      lastInitializedPreviewTableRef.current === initialPreviewTable
    ) {
      return;
    }

    lastInitializedPreviewTableRef.current = initialPreviewTable;
    hasSetForceRefreshForInitialTableRef.current = false;

    // Try to get the data source from Redux first (it should have tables)
    const reduxDataSource = findDataSourceInRedux(
      effectiveDataSource.id,
      dataSourceType,
      dataSourcesByType,
      dataSources
    );
    const dataSourceWithTables = reduxDataSource || effectiveDataSource;

    setLocalDataSource(dataSourceWithTables);
    setPreviewTable(initialPreviewTable);
    setDisplayPreviewTable(initialPreviewTable);
    setStep('tables');
    setExpandAllTables(true);

    // Restore edited values from metadata if available
    const metadata = vgTableMetadata[initialPreviewTable];
    if (metadata) {
      if (metadata.editedName) {
        setEditedResourceName(metadata.editedName);
      }
      if (metadata.editedFieldLabels) {
        setEditedFieldLabels(metadata.editedFieldLabels);
      }
      // Set preview data with cached fields for immediate display
      setPreviewData({
        fields: metadata.fields,
        previewRows: [],
        allResultsIncluded: true,
      });
      lastProcessedQueryRef.current = null;
      // Only set forceRefresh once per initialPreviewTable
      if (!hasSetForceRefreshForInitialTableRef.current) {
        setForceRefresh(true);
        hasSetForceRefreshForInitialTableRef.current = true;
      }
    } else {
      // No metadata - clear preview data and force a fresh query
      setPreviewData(null);
      lastProcessedQueryRef.current = null;
      if (!hasSetForceRefreshForInitialTableRef.current) {
        setForceRefresh(true);
        hasSetForceRefreshForInitialTableRef.current = true;
      }
    }

    // If tables aren't loaded, refresh them
    if (!dataSourceWithTables.tables?.length) {
      dispatch(refreshDataSourceTables(effectiveDataSource.id));
    }
  }, [
    initialPreviewTable,
    effectiveDataSource,
    dataSourceType,
    dataSourcesByType,
    dataSources,
    vgTableMetadata,
    dispatch,
    setEditedFieldLabels,
    setEditedResourceName,
  ]);

  // Effect 3: Skip directly to tables step
  React.useEffect(() => {
    if (!skipToTables || localDataSource) {
      return;
    }

    let dataSourceToUse: DataSource | null = null;

    if (dataSourceId) {
      // For sample music, look in the actual MySQL data sources
      if (dataSourceType === SAMPLE_MUSIC_DATA_SOURCE_TYPE) {
        const mysqlDataSources =
          dataSourcesByType[SAMPLE_MUSIC_DATA_SOURCE.type] || [];
        dataSourceToUse =
          mysqlDataSources.find((ds) => ds.id === dataSourceId) || null;
      } else {
        dataSourceToUse =
          dataSources.find((ds) => ds.id === dataSourceId) || null;
      }
    } else if (contextDataSource) {
      const reduxDataSource = findDataSourceInRedux(
        contextDataSource.id,
        dataSourceType,
        dataSourcesByType,
        dataSources
      );
      dataSourceToUse = reduxDataSource || contextDataSource;
    } else if (dataSourceType === SAMPLE_MUSIC_DATA_SOURCE_TYPE) {
      const mysqlDataSources =
        dataSourcesByType[SAMPLE_MUSIC_DATA_SOURCE.type] || [];
      dataSourceToUse =
        mysqlDataSources.find((ds) => ds.id === SAMPLE_MUSIC_DATA_SOURCE.id) ||
        null;
    } else {
      dataSourceToUse = dataSources[0] || null;
    }

    if (dataSourceToUse) {
      setLocalDataSource(dataSourceToUse);
      setStep('tables');
      setExpandAllTables(true);

      if (!dataSourceToUse.tables?.length) {
        dispatch(refreshDataSourceTables(dataSourceToUse.id));
      }
    }
  }, [
    skipToTables,
    localDataSource,
    dataSourceId,
    contextDataSource,
    dataSources,
    dataSourceType,
    dataSourcesByType,
    dispatch,
  ]);

  // Effect 4: Initialize for custom query preview mode
  React.useEffect(() => {
    if (
      !initialCustomQuery ||
      !effectiveDataSource ||
      lastInitializedCustomQueryRef.current === initialCustomQuery
    ) {
      return;
    }

    const customQuery = customSqlQueries.find(
      (q) => q.name === initialCustomQuery
    );

    if (!customQuery) {
      return;
    }

    lastInitializedCustomQueryRef.current = initialCustomQuery;

    const reduxDataSource = findDataSourceInRedux(
      effectiveDataSource.id,
      dataSourceType,
      dataSourcesByType,
      dataSources
    );
    const dataSourceWithTables = reduxDataSource || effectiveDataSource;

    setLocalDataSource(dataSourceWithTables);
    setStep('tables');
    setIsUsingCustomQuery(true);
    setCustomQueryText(customQuery.query);
    setSubmittedCustomQuery(customQuery.query);
    lastProcessedQueryRef.current = null;
    setForceRefresh(true);

    if (customQuery.fields) {
      setPreviewData({
        fields: customQuery.fields,
        previewRows: [],
        allResultsIncluded: true,
      });
      setEditedResourceName(customQuery.name);
      const savedFieldLabels: Record<string, string> = {};
      customQuery.fields.forEach((field) => {
        savedFieldLabels[field.value] = field.label;
      });
      setEditedFieldLabels(savedFieldLabels);
    }

    if (!dataSourceWithTables.tables?.length) {
      dispatch(refreshDataSourceTables(effectiveDataSource.id));
    }
  }, [
    initialCustomQuery,
    effectiveDataSource,
    customSqlQueries,
    dataSourceType,
    dataSourcesByType,
    dataSources,
    dispatch,
    setEditedFieldLabels,
    setEditedResourceName,
  ]);

  // Sync localDataSource with Redux when tables are loaded
  React.useEffect(() => {
    if (localDataSource) {
      const updatedDataSource = findDataSourceInRedux(
        localDataSource.id,
        dataSourceType,
        dataSourcesByType,
        dataSources
      );

      // Only sync from Redux if:
      // 1. Redux has the data source AND
      // 2. Redux has tables loaded (not empty) AND
      // 3. The tables reference is different (to avoid unnecessary updates)
      // This prevents overwriting localDataSource (which may have tables from contextDataSource)
      // with a Redux version that hasn't loaded tables yet
      if (
        updatedDataSource &&
        updatedDataSource.tables &&
        updatedDataSource.tables.length > 0 &&
        updatedDataSource.tables !== localDataSource.tables
      ) {
        setLocalDataSource(updatedDataSource);
      }
    }
  }, [dataSources, dataSourcesByType, localDataSource, dataSourceType]);

  const tables = React.useMemo(() => {
    const tableList = [...(localDataSource?.tables || [])];
    return tableList;
  }, [localDataSource?.tables]);

  const { handleCheckTable, handleCheckAllInCatalog, handleCheckAllInSchema } =
    useTableChecking({ tables, setCheckedVgTables });

  // Auto-select first table when tables are loaded and nothing is selected
  React.useEffect(() => {
    if (
      step === 'tables' &&
      tables.length > 0 &&
      !previewTable &&
      !isUsingCustomQuery &&
      !initialPreviewTable &&
      !initialCustomQuery
    ) {
      setPreviewTable(getDataSourceTableKey(tables[0]));
      setDisplayPreviewTable(getDataSourceTableKey(tables[0]));
      setForceRefresh(true);
    }
  }, [
    step,
    tables,
    previewTable,
    isUsingCustomQuery,
    initialPreviewTable,
    initialCustomQuery,
  ]);

  const nativeQuoteCharacter = useReduxSelector(
    dataSourceNativeQuoteCharacterSelector(dataSourceType, connection)
  );

  // Virtual graph properties for advanced options suggestions
  // virtualGraphPropertiesSelector is a factory with a non-serializable arg, so
  // it can't be memoized at the source; memoize the instance here instead.
  const propertyDescriptionsSelector = React.useMemo(
    () => virtualGraphPropertiesSelector(connection, dataSourceType),
    [connection, dataSourceType]
  );
  const propertyDescriptions = useReduxSelector(propertyDescriptionsSelector);

  const STANDARD_OPTIONS = React.useMemo(
    () => new Set(Object.values(VirtualGraphOption)),
    []
  );

  const propertySuggestions = React.useMemo(() => {
    return Object.entries(propertyDescriptions)
      .filter(
        ([property]) => !STANDARD_OPTIONS.has(property as VirtualGraphOption)
      )
      .map(([property]) => property)
      .sort();
  }, [propertyDescriptions, STANDARD_OPTIONS]);

  const advancedPropertyMetadata = React.useMemo(() => {
    const result: Record<
      string,
      NonNullable<(typeof propertyDescriptions)[string]>
    > = {};
    Object.entries(propertyDescriptions).forEach(([property, metadata]) => {
      if (!STANDARD_OPTIONS.has(property as VirtualGraphOption) && metadata) {
        result[property] = metadata;
      }
    });
    return result;
  }, [propertyDescriptions, STANDARD_OPTIONS]);

  // Merge advanced options into a combined options object
  const mergedVirtualGraphOptions = React.useMemo(() => {
    const merged: ProjectVirtualGraphOptions = { ...virtualGraphOptions };
    advancedVgOptions.forEach(({ property, value }) => {
      if (property.trim()) {
        (merged as Record<string, string | boolean | undefined>)[property] =
          value || undefined;
      }
    });
    return merged;
  }, [virtualGraphOptions, advancedVgOptions]);

  // Generate query for preview - returns undefined when no query should run
  const previewQuery = React.useMemo(() => {
    // Never run the expensive preview query when previews are disabled (or
    // before options load, when we cannot yet tell); column headers come from
    // the catalog instead.
    if (!optionsLoaded || previewsDisabled) {
      return undefined;
    }

    // If we're in custom query mode and have a submitted query, use that
    if (isUsingCustomQuery && submittedCustomQuery) {
      const query = submittedCustomQuery.trim() || undefined;
      return query;
    }

    // If we're in table mode, generate query from table
    if (!isUsingCustomQuery && previewTable && localDataSource) {
      const table = tables.find(
        (t) => getDataSourceTableKey(t) === previewTable
      );
      if (table && nativeQuoteCharacter !== null) {
        const query = generateSqlQueryForTable({
          table,
          nativeQuoteCharacter,
          options: mergedVirtualGraphOptions,
          limit: true,
        });
        return query;
      }
    }

    return undefined;
  }, [
    previewTable,
    localDataSource,
    tables,
    nativeQuoteCharacter,
    submittedCustomQuery,
    isUsingCustomQuery,
    mergedVirtualGraphOptions,
    optionsLoaded,
    previewsDisabled,
  ]);

  const runningQuery = useProjectDataSourceQuery({
    dataSourceId: localDataSource?.id,
    existingFields: previewData?.fields || EMPTY_FIELDS,
    forceRefresh,
    query: previewQuery,
    virtualGraphOptions: undefined,
  });

  // Use shared hook for metadata fetch queue
  const {
    metadataFetchQueue,
    setMetadataFetchQueue,
    tablesNeedingMetadataRef,
  } = useMetadataFetchQueue({
    checkedVgTables,
    vgTableMetadata,
    localDataSource,
    isQuerying: runningQuery.isQuerying,
    isUsingCustomQuery,
    nativeQuoteCharacter,
    tables,
    initialPreviewTable,
    previewTable,
    setPreviewTable,
    setForceRefresh,
    enabled: step === 'tables' && !previewsDisabled,
    isManualPreviewRef,
  });

  const didIsQueryingChange = useDidValueChange(runningQuery.isQuerying);

  React.useEffect(() => {
    if (runningQuery.isQuerying && forceRefresh) {
      setForceRefresh(false);
    }
  }, [runningQuery.isQuerying, forceRefresh]);

  // Update preview data when query completes
  React.useEffect(() => {
    // Must have a query to process
    if (!previewQuery) {
      return;
    }

    // Only process when query is not running and we have results
    if (runningQuery.isQuerying) {
      return;
    }

    // If forceRefresh is true, we're waiting for a new query to start
    // Don't process stale results
    if (forceRefresh) {
      return;
    }

    // Check for errors
    if (runningQuery.errors.length > 0) {
      // Only surface the error for the view the user is actually looking at —
      // background metadata-fetch queries (which temporarily swap previewTable)
      // should not clobber the displayed preview with an error.
      const isErrorForDisplayedView =
        isManualPreviewRef.current ||
        previewTable === displayPreviewTable ||
        (isUsingCustomQuery && Boolean(submittedCustomQuery));
      if (isErrorForDisplayedView) {
        setErrorText(runningQuery.errors.map((e) => e.message).join('\n'));
        setPreviewData(null); // Clear preview on error
      }
      lastProcessedQueryRef.current = null; // Allow retry
      return;
    }

    // Clear any previous error now that the query succeeded
    setErrorText((prev) => (prev ? '' : prev));

    // Skip if we've already processed this exact query
    if (lastProcessedQueryRef.current === previewQuery) {
      return;
    }

    // For custom queries, we need fields to show preview
    // For table queries, fields might be empty if table has no structure
    if (isUsingCustomQuery && runningQuery.fields.length === 0) {
      return;
    }

    // Only update if query just finished (detected by didIsQueryingChange) OR if we have new fields/rows
    // This handles the case where didIsQueryingChange might not fire but results are available
    const hasNewResults =
      runningQuery.fields.length > 0 || runningQuery.rows.length > 0;
    if (!didIsQueryingChange && !hasNewResults) {
      return;
    }

    // Only mark the query as processed if we actually received results from the query.
    // If we only have cached data, don't mark as processed so we can update when real data arrives.
    if (hasNewResults) {
      lastProcessedQueryRef.current = previewQuery;
    }

    // Update preview data with query
    const shouldUpdatePreviewDisplay =
      isManualPreviewRef.current ||
      previewTable === displayPreviewTable ||
      (isUsingCustomQuery && submittedCustomQuery);
    if (shouldUpdatePreviewDisplay) {
      setPreviewData((prev) => ({
        fields:
          runningQuery.fields.length > 0
            ? runningQuery.fields
            : prev?.fields || [],
        previewRows:
          runningQuery.rows.length > 0
            ? runningQuery.rows
            : prev?.previewRows || [],
        allResultsIncluded: runningQuery.allResultsIncluded,
      }));
    }

    // Store metadata for table (not for custom queries - those are stored when saving)
    if (previewTable && !isUsingCustomQuery) {
      setVgTableMetadata((prev: Record<string, VgTableMetadata>) => {
        const existing = prev[previewTable];
        // Preserve edited values if they exist
        return {
          ...prev,
          [previewTable]: {
            tableName: previewTable,
            fields: runningQuery.fields,
            rowCount: runningQuery.rows.length,
            previewRows: runningQuery.rows,
            editedName: existing?.editedName,
            editedFieldLabels: existing?.editedFieldLabels,
          },
        };
      });

      // Restore edited values if they exist in metadata
      const existingMetadata = vgTableMetadata[previewTable];
      if (existingMetadata?.editedName) {
        setEditedResourceName(existingMetadata.editedName);
      } else {
        setEditedResourceName(null);
      }
      if (existingMetadata?.editedFieldLabels) {
        setEditedFieldLabels(existingMetadata.editedFieldLabels);
      } else {
        setEditedFieldLabels({});
      }

      // If this was a metadata fetch (table is in queue), remove it and process next
      if (metadataFetchQueue.includes(previewTable)) {
        const currentTable = previewTable;
        const remainingQueue = metadataFetchQueue.filter(
          (t) => t !== currentTable
        );
        setMetadataFetchQueue(remainingQueue);
        tablesNeedingMetadataRef.current.delete(currentTable);

        if (previewTable === initialPreviewTable) {
          return;
        }

        if (remainingQueue.length > 0) {
          const nextTable = remainingQueue[0];
          setPreviewTable(nextTable);
          lastProcessedQueryRef.current = null;
          setForceRefresh(true);
        } else if (!displayPreviewTable) {
          setPreviewTable(null);
          setPreviewData(null);
        } else if (previewTable !== displayPreviewTable) {
          setPreviewTable(displayPreviewTable);
          lastProcessedQueryRef.current = null;
          setForceRefresh(true);
        }
      }
    }
  }, [
    didIsQueryingChange,
    runningQuery.isQuerying,
    runningQuery.fields,
    runningQuery.rows,
    runningQuery.allResultsIncluded,
    runningQuery.errors,
    previewQuery,
    previewTable,
    displayPreviewTable,
    isUsingCustomQuery,
    submittedCustomQuery,
    setVgTableMetadata,
    forceRefresh,
    metadataFetchQueue,
    setMetadataFetchQueue,
    vgTableMetadata,
    initialPreviewTable,
    setEditedFieldLabels,
    setEditedResourceName,
    tablesNeedingMetadataRef,
  ]);

  // When previews are disabled, populate table metadata from catalog columns
  // (instead of the skipped preview query) so checked tables can be added.
  // Existing edited names/labels are preserved.
  React.useEffect(() => {
    if (!previewsDisabled) {
      return;
    }
    if (!Object.keys(catalogColumnsByTableKey).length) {
      return;
    }
    setVgTableMetadata((prev: Record<string, VgTableMetadata>) => {
      let changed = false;
      const next = { ...prev };
      tables.forEach((table) => {
        const key = getDataSourceTableKey(table);
        const columns = catalogColumnsByTableKey[key];
        if (!columns) {
          return;
        }
        const fields = catalogColumnsToFields(columns);
        const existing = prev[key];
        if (existing && isEqual(existing.fields, fields)) {
          return;
        }
        next[key] = {
          tableName: key,
          fields,
          rowCount: 0,
          previewRows: [],
          editedName: existing?.editedName,
          editedFieldLabels: existing?.editedFieldLabels,
        };
        changed = true;
      });
      return changed ? next : prev;
    });
  }, [previewsDisabled, catalogColumnsByTableKey, tables, setVgTableMetadata]);

  // When previews are disabled, drive the preview display from the catalog
  // columns of the selected table (headers only, no sample rows).
  React.useEffect(() => {
    if (!previewsDisabled || isUsingCustomQuery || !displayPreviewTable) {
      return;
    }
    const columns = catalogColumnsByTableKey[displayPreviewTable];
    if (!columns) {
      return;
    }
    const fields = catalogColumnsToFields(columns);
    setPreviewData((prev) =>
      prev && isEqual(prev.fields, fields) && prev.previewRows.length === 0
        ? prev
        : { fields, previewRows: [], allResultsIncluded: true }
    );
  }, [
    previewsDisabled,
    isUsingCustomQuery,
    displayPreviewTable,
    catalogColumnsByTableKey,
  ]);

  const handleBack = React.useCallback(() => {
    // If we came from preview mode, close the panel to return to previous panel.
    // closePanel pops the PanelStack (exists when this panel is NOT the root).
    // resetDialogState is the fallback for root panels (e.g., DataConnectionPanel
    // flow where it closes the VG config drawer).
    if (initialPreviewTable || initialCustomQuery) {
      if (closePanel) {
        closePanel();
      } else {
        resetDialogState();
      }
      return;
    }

    if (step === 'tables') {
      // If we skipped to tables (e.g., sample music data source), close the panel.
      if (skipToTables || dataSourceType === SAMPLE_MUSIC_DATA_SOURCE_TYPE) {
        if (closePanel) {
          closePanel();
        } else {
          resetDialogState();
        }
        return;
      }
      // Go back to connection selection
      setStep('connection');
      setLocalDataSource(null);
      setPreviewTable(null);
      setDisplayPreviewTable(null);
      setPreviewData(null);
      setIsUsingCustomQuery(false);
      setCustomQueryText('');
      setSubmittedCustomQuery(null);
      lastProcessedQueryRef.current = null;
      setCheckedVgTables([]);
      setVgTableMetadata({});
      setCustomSqlQueries([]);
      setVgPreviewItem(null);
    } else {
      // Close the panel entirely
      closePanel?.();
    }
  }, [
    step,
    closePanel,
    initialPreviewTable,
    initialCustomQuery,
    skipToTables,
    dataSourceType,
    resetDialogState,
    setCheckedVgTables,
    setCustomSqlQueries,
    setVgPreviewItem,
    setVgTableMetadata,
  ]);

  const handleSelectDataSource = React.useCallback(
    async (dataSource: DataSource) => {
      if (!connection || isConnecting) {
        return;
      }

      setIsConnecting(true);

      try {
        await dispatch(refreshDataSourceTables(dataSource.id));
        // Get the updated data source from Redux (with tables populated)
        const updatedDataSource = dataSources.find(
          (ds) => ds.id === dataSource.id
        );
        setLocalDataSource(updatedDataSource || dataSource);
        setStep('tables');
        setExpandAllTables(true);
      } catch (error) {
        console.error('Failed to refresh data source tables:', error);
      } finally {
        setIsConnecting(false);
      }
    },
    [connection, dataSources, dispatch, isConnecting]
  );

  const handleSelectSampleDataSource = React.useCallback(async () => {
    const dataSource = await createOrRefreshSampleDataSource();
    if (dataSource) {
      // Use the existing data source from Redux if available, otherwise use the returned one
      setLocalDataSource(existingSampleDataSource || dataSource);
      setStep('tables');
      setExpandAllTables(true);
    }
  }, [createOrRefreshSampleDataSource, existingSampleDataSource]);

  const handleSelectNewConnection = React.useCallback(() => {
    openPanel?.({
      renderPanel: DataSourceCreationPanel,
      props: {
        dataSourceType,
        contextOverrides: {
          setDialogTitle,
          setHeaderBackButton,
          setFullSize,
          setFooterOptions,
        },
      },
    });
  }, [
    openPanel,
    dataSourceType,
    setDialogTitle,
    setHeaderBackButton,
    setFullSize,
    setFooterOptions,
  ]);

  React.useEffect(() => {
    if (connection && step === 'connection') {
      dataSources.forEach((dataSource) => {
        if (!dataSource.options) {
          dataSourcesStardogRequestDispatchers.requestDataSourceOptions(
            dataSource.id,
            connection
          );
        }
      });
    }
  }, [connection, dataSources, step]);

  const handlePreviewTable = React.useCallback(
    (tableName: string) => {
      isManualPreviewRef.current = true; // Mark as user-initiated preview
      setPreviewTable(tableName);
      setDisplayPreviewTable(tableName); // Also update what user sees
      setErrorText('');
      setSubmittedCustomQuery(null); // Clear custom query when selecting table
      // Reset edited values when switching tables
      setEditedResourceName(null);
      setEditedFieldLabels({});

      if (previewsDisabled) {
        // No preview query runs for skip-preview sources; show the catalog
        // columns for this table immediately (headers only) instead of blanking
        // the view.
        const columns = catalogColumnsByTableKey[tableName];
        setPreviewData({
          fields: columns ? catalogColumnsToFields(columns) : [],
          previewRows: [],
          allResultsIncluded: true,
        });
      } else {
        setPreviewData(null);
        lastProcessedQueryRef.current = null; // Reset so new query will be processed
        setForceRefresh(true); // Force query to run
      }
    },
    [
      previewsDisabled,
      catalogColumnsByTableKey,
      setEditedFieldLabels,
      setEditedResourceName,
    ]
  );

  const handleSelectTable = React.useCallback(
    (tableName: string) => {
      handlePreviewTable(tableName);
    },
    [handlePreviewTable]
  );

  const isSingleSelectMode = Boolean(initialPreviewTable);

  const handleCustomQueryChange = React.useCallback(
    (query: string) => {
      setCustomQueryText(query);
      // Clear submitted query when text changes
      if (submittedCustomQuery) {
        setSubmittedCustomQuery(null);
        setPreviewData(null);
        setErrorText('');
        lastProcessedQueryRef.current = null;
      }
    },
    [submittedCustomQuery]
  );

  const handleRunCustomQuery = React.useCallback(
    (queryText?: string) => {
      // Use the passed query text if available, otherwise fall back to state
      const queryToUse = queryText?.trim() || customQueryText.trim();
      if (queryToUse && localDataSource) {
        // Clear preview state (but keep checked tables)
        setPreviewTable(null);
        setPreviewData(null);
        setErrorText('');
        // Clear the last processed query ref so the new query will definitely run
        lastProcessedQueryRef.current = null;
        // Set the submitted query - this will trigger the query to run
        setSubmittedCustomQuery(queryToUse);
        // Force refresh to ensure the query runs
        setForceRefresh(true);
      }
    },
    [customQueryText, localDataSource]
  );

  const handleRefreshPreview = React.useCallback(() => {
    setErrorText('');
    lastProcessedQueryRef.current = null;
    setForceRefresh(true);
    // Re-fetch the data source options so a changed previews-disabled flag is
    // picked up on refresh.
    const activeDataSource = reduxDataSource || localDataSource;
    if (connection && activeDataSource) {
      dataSourcesStardogRequestDispatchers.requestDataSourceOptions(
        activeDataSource.id,
        connection
      );
    }
    // When previews are disabled no preview query runs; re-fetch the catalog
    // columns instead so the refresh button updates them.
    if (previewsDisabled) {
      refetchCatalogColumns();
    }
  }, [
    connection,
    localDataSource,
    previewsDisabled,
    reduxDataSource,
    refetchCatalogColumns,
  ]);

  const handleModeChange = React.useCallback(
    (isCustomQuery: boolean) => {
      setIsUsingCustomQuery(isCustomQuery);
      setErrorText('');
      if (isCustomQuery) {
        // Switching to custom query mode - clear preview state (but keep checked tables)
        setPreviewTable(null);
        setPreviewData(null);
        lastProcessedQueryRef.current = null;

        // If we have a previous query, auto-rerun it
        if (customQueryText.trim()) {
          setSubmittedCustomQuery(customQueryText.trim());
          setForceRefresh(true);
        } else {
          setSubmittedCustomQuery(null);
        }
      } else {
        // Switching back to table mode - clear preview state but preserve query text
        // so user can switch back to custom query mode and see their query
        setSubmittedCustomQuery(null);
        setPreviewData(null);
        lastProcessedQueryRef.current = null;
        // If we were viewing an existing custom query, remove it since user is switching away
        if (initialCustomQuery) {
          setCustomSqlQueries((prev) =>
            prev.filter((q) => q.name !== initialCustomQuery)
          );
        }
      }
    },
    [customQueryText, initialCustomQuery, setCustomSqlQueries]
  );

  const handleAdd = React.useCallback(() => {
    // Filter out checked tables that no longer exist in the current table list
    // (can happen after a metadata refresh removes tables) or whose column
    // metadata was lost (e.g. a table was dropped and refreshed while checked
    // but not actively selected, clearing its fields)
    setCheckedVgTables((prev) =>
      prev.filter((t) => {
        const tableKey = getDataSourceTableKey(t);
        const existsInTables = tables.some(
          (table) => getDataSourceTableKey(table) === tableKey
        );
        const hasColumns = Boolean(vgTableMetadata[tableKey]?.fields?.length);
        return existsInTables && hasColumns;
      })
    );

    if (localDataSource) {
      setSelectedDataSource(localDataSource);
      setSelectedDataSourceType(dataSourceType);
    }

    // Only save custom query if we're in custom query mode and have results
    if (
      isUsingCustomQuery &&
      submittedCustomQuery &&
      previewData &&
      previewData.fields.length > 0
    ) {
      // Use edited field labels if available
      const fieldsWithEditedLabels = previewData.fields.map((field) => ({
        ...field,
        label: editedFieldLabels[field.value] ?? field.label,
      }));

      if (initialCustomQuery) {
        // Update existing query - use edited name if available, otherwise keep original
        const name = editedResourceName || undefined; // undefined means keep original
        setCustomSqlQueries((prev) =>
          prev.map((q) =>
            q.name === initialCustomQuery
              ? {
                  query: submittedCustomQuery,
                  name: name || q.name, // Use edited name if provided, otherwise keep original
                  fields: fieldsWithEditedLabels,
                  rowCount: previewData.previewRows.length,
                  previewRows: previewData.previewRows,
                }
              : q
          )
        );
      } else {
        // Add new query - use edited name if available, otherwise generate default
        const name =
          editedResourceName || `Custom Query ${customSqlQueries.length + 1}`;
        setCustomSqlQueries((prev) => [
          ...prev,
          {
            query: submittedCustomQuery,
            name,
            fields: fieldsWithEditedLabels,
            rowCount: previewData.previewRows.length,
            previewRows: previewData.previewRows,
          },
        ]);
      }
      // Don't clear checked tables - allow both tables and custom queries to coexist
    }
    // If not in custom query mode, the checked tables are already saved in context

    closePanel?.();
  }, [
    tables,
    vgTableMetadata,
    setCheckedVgTables,
    localDataSource,
    dataSourceType,
    setSelectedDataSource,
    setSelectedDataSourceType,
    isUsingCustomQuery,
    submittedCustomQuery,
    previewData,
    customSqlQueries.length,
    initialCustomQuery,
    setCustomSqlQueries,
    editedResourceName,
    editedFieldLabels,
    closePanel,
  ]);

  const handleCancel = React.useCallback(() => {
    setCheckedVgTables([]);
    setCustomSqlQueries([]);
    setVgTableMetadata({});
    setVgPreviewItem(null);
    closePanel?.();
  }, [
    setCheckedVgTables,
    setCustomSqlQueries,
    setVgTableMetadata,
    setVgPreviewItem,
    closePanel,
  ]);

  const hasPreviewContent = React.useMemo(() => {
    if (isUsingCustomQuery) {
      // In custom query mode, show preview if we have submitted query and preview data with fields
      return Boolean(
        submittedCustomQuery && previewData && previewData.fields.length > 0
      );
    }
    // In table mode, show preview if we have a table and preview data
    return Boolean(previewTable && previewData);
  }, [isUsingCustomQuery, submittedCustomQuery, previewTable, previewData]);

  const title =
    step === 'connection'
      ? copy.components.virtualGraphPreviewDialog.connectionSelection.title
      : copy.components.projectResourceDialog.addDataSourcePanel.title;

  useDialogPanelSetup(
    { setDialogTitle, setHeaderBackButton, setFullSize, setFooterOptions },
    {
      title,
      fullSize: step === 'tables',
      onBack: handleBack,
    }
  );

  const checkedTableNames = React.useMemo(() => {
    return checkedVgTables.map((t: DataSourceTable) =>
      getDataSourceTableKey(t)
    );
  }, [checkedVgTables]);

  // A checked table the catalog has no columns for can't be mapped; block Add
  // (and show a notice) rather than silently dropping it on save.
  const hasCheckedTableWithoutColumns = React.useMemo(
    () =>
      checkedVgTables.some((t) =>
        tableHasNoCatalogColumns(getDataSourceTableKey(t))
      ),
    [checkedVgTables, tableHasNoCatalogColumns]
  );

  React.useEffect(() => {
    if (step === 'tables') {
      const isViewOnlyMode = initialPreviewTable && !isUsingCustomQuery;
      if (isViewOnlyMode) {
        setFooterOptions({});
        return;
      }

      // Show "Save" when viewing an existing custom query, "Add" otherwise
      const isEditingExistingQuery = initialCustomQuery && isUsingCustomQuery;
      const nextLabel = isEditingExistingQuery
        ? copy.components.projectResourceDialog.saveButton
        : copy.components.projectResourceDialog.addDataSourcePanel.addButton;

      setFooterOptions({
        onCancel: handleCancel,
        onNext: handleAdd,
        nextLabel,
        isNextDisabled:
          isCatalogUnavailable ||
          hasCheckedTableWithoutColumns ||
          ((isUsingCustomQuery
            ? !(
                submittedCustomQuery &&
                previewData &&
                previewData.fields.length > 0
              )
            : checkedVgTables.length === 0) &&
            customSqlQueries.length === 0),
      });
    }
  }, [
    step,
    handleCancel,
    handleAdd,
    checkedVgTables.length,
    customSqlQueries.length,
    hasCheckedTableWithoutColumns,
    isCatalogUnavailable,
    isUsingCustomQuery,
    submittedCustomQuery,
    previewData,
    initialCustomQuery,
    initialPreviewTable,
    setFooterOptions,
  ]);

  if (step === 'connection') {
    const isSample = dataSourceType === SAMPLE_MUSIC_DATA_SOURCE_TYPE;

    if (isSample) {
      // Auto-select sample data source
      if (!isSampleLoading && !localDataSource) {
        handleSelectSampleDataSource();
      }

      return (
        <SelectionPanelContainer
          subtitle={
            copy.components.virtualGraphPreviewDialog.connectionSelection
              .subtitle
          }
        >
          <div className={styles.panelLoading}>
            <Loader size={72} />
          </div>
        </SelectionPanelContainer>
      );
    }

    return (
      <ConnectionSelectionStep
        dataSources={dataSources}
        dataSourceType={dataSourceType}
        isLoading={isConnecting}
        onSelectDataSource={handleSelectDataSource}
        onSelectNewConnection={handleSelectNewConnection}
      />
    );
  }

  const asideContent = (
    <>
      <div
        data-testid={TEST_IDS.projectNewResourceDialog.addDataSource.tableTree}
      >
        <VirtualGraphTableSelection
          checkedTables={isSingleSelectMode ? undefined : checkedTableNames}
          customQuery={customQueryText}
          customQueryDisabled={previewsDisabled}
          expandAllTables={expandAllTables}
          isMetadataRefreshable={isMetadataRefreshable}
          isQueryRunning={runningQuery.isQuerying}
          isRefreshingMetadata={isRefreshingMetadata}
          isUsingCustomQuery={isUsingCustomQuery}
          multiSelect={!isSingleSelectMode}
          onCheckAllInCatalog={
            isSingleSelectMode ? undefined : handleCheckAllInCatalog
          }
          onCheckAllInSchema={
            isSingleSelectMode ? undefined : handleCheckAllInSchema
          }
          onCheckTable={isSingleSelectMode ? undefined : handleCheckTable}
          onCustomQueryChange={handleCustomQueryChange}
          onModeChange={handleModeChange}
          onPreviewTable={isSingleSelectMode ? undefined : handlePreviewTable}
          onRefreshMetadata={refreshDataSourceTypeMetadata}
          onRunQuery={handleRunCustomQuery}
          onSelectTable={isSingleSelectMode ? handleSelectTable : undefined}
          previewTable={isSingleSelectMode ? undefined : displayPreviewTable}
          selectedTable={isSingleSelectMode ? displayPreviewTable : undefined}
          tables={tables}
        />
      </div>

      <Accordion
        icon="left"
        isOpen={isPropertiesOpen}
        label={
          copy.components.projectResourceDialog.projectVirtualGraphPanel.form
            .resourceProperties
        }
        onInteraction={setIsPropertiesOpen}
        size="md"
        testId={TEST_IDS.projectVirtualGraphPanel.resourceProperties.accordion}
      >
        <VirtualGraphOptionsForm
          onSetVirtualGraphOptions={setVirtualGraphOptions}
          virtualGraphOptions={virtualGraphOptions}
        />

        <Accordion
          icon="left"
          isOpen={isAdvancedOpen}
          label={copy.components.virtualGraphPreviewDialog.advancedOptions.title(
            advancedVgOptions.filter((opt) => opt.property.trim() !== '').length
          )}
          onInteraction={setIsAdvancedOpen}
          size="md"
          testId={
            TEST_IDS.projectVirtualGraphPanel.resourceProperties.advancedOptions
              .accordion
          }
        >
          <AdvancedOptions
            onOptionsChange={setAdvancedVgOptions}
            options={advancedVgOptions}
            propertyMetadata={advancedPropertyMetadata}
            propertySuggestions={propertySuggestions}
          />
        </Accordion>
      </Accordion>
    </>
  );

  const matchedPreviewTable = tables.find(
    (t) => getDataSourceTableKey(t) === displayPreviewTable
  );
  const previewTableFrom = {
    name: matchedPreviewTable?.name || '',
    text: matchedPreviewTable?.name || '',
    value: displayPreviewTable || '',
    catalog: matchedPreviewTable?.catalog,
    schema: matchedPreviewTable?.schema,
  };

  const previewVirtualGraph: ProjectVirtualGraph | null = hasPreviewContent
    ? {
        id: 'preview',
        name: currentResourceName,
        dataSourceId: localDataSource?.id || '',
        dataSourceType,
        dataSourceEndpoint:
          localDataSource?.options?.[DataSourceOption.ENDPOINT] || '',
        connectionId: connection?.id || null,
        from: submittedCustomQuery || previewTableFrom,
        fields: currentFields,
        previewRows: previewData?.previewRows || [],
        allResultsIncluded: previewData?.allResultsIncluded ?? false,
        dateLastUpdated: Date.now(),
        options: mergedVirtualGraphOptions,
      }
    : null;

  const emptyStateCopy = isUsingCustomQuery
    ? copy.components.virtualGraphPreviewDialog.noCustomQuery
    : copy.components.virtualGraphPreviewDialog.noTableSelected;

  const getMainContent = () => {
    // Show loading while a query runs (prevents empty/error state flash during transitions)
    if (runningQuery.isQuerying || (previewsDisabled && isCatalogLoading)) {
      return <PreviewLoadingState />;
    }
    if (errorText) {
      return (
        <PreviewErrorState
          message={errorText}
          title={copy.components.virtualGraphPreviewDialog.errorTitle}
        />
      );
    }
    if (isCatalogUnavailable) {
      return (
        <PreviewNoSelectionState
          subtitle={
            copy.components.virtualGraphPreviewDialog.catalogUnavailable
              .subtitle
          }
          title={
            copy.components.virtualGraphPreviewDialog.catalogUnavailable.title
          }
        />
      );
    }
    if (
      !isUsingCustomQuery &&
      displayPreviewTable &&
      tableHasNoCatalogColumns(displayPreviewTable)
    ) {
      return (
        <PreviewNoSelectionState
          subtitle={
            copy.components.virtualGraphPreviewDialog.noColumns.subtitle
          }
          title={copy.components.virtualGraphPreviewDialog.noColumns.title}
        />
      );
    }
    if (previewVirtualGraph) {
      return (
        <VirtualGraphPreview
          error=""
          onFieldLabelChange={handleFieldLabelChange}
          onRefresh={handleRefreshPreview}
          onResourceNameChange={handleResourceNameChange}
          previewsDisabled={previewsDisabled}
          resourceName={currentResourceName}
          rowCount={previewData?.previewRows?.length ?? null}
          virtualGraph={previewVirtualGraph}
        />
      );
    }
    return (
      <PreviewNoSelectionState
        subtitle={emptyStateCopy.subtitle}
        title={emptyStateCopy.title}
      />
    );
  };

  const mainContent = getMainContent();

  return (
    <PanelWithSidebar asideContent={asideContent} mainContent={mainContent} />
  );
};
